import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
    formatDateHM,
    formatDateHMRange,
    formatDayMonth,
    isToday,
} from "crqlar-react-common/dist/utils/date.util";
import {daySlices} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    FLOORPLAN_DAY_SLICES,
    type FloorplanDaySlice,
} from "../../floorplanState";
import {useTimeframe} from "../hooks/useTimeframe";
import DayAvailability from "./DayAvailability";

const isFloorplanDaySlice = (value: string): value is FloorplanDaySlice => (
    FLOORPLAN_DAY_SLICES.includes(value as FloorplanDaySlice)
);

const Timeframe: React.FC = () => {
    const {t} = useTranslation();
    const {
        date,
        timeframeStart,
        timeframeEnd,
        isRange,
        reservationDaySlice,
        customTimeframe,
        setReservationDaySlice,
    } = useTimeframe();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const sliceKey: FloorplanDaySlice = isFloorplanDaySlice(reservationDaySlice)
        ? reservationDaySlice
        : "now";
    const selectedDateIsToday = isToday(date);
    const isAtNow = selectedDateIsToday && sliceKey === "now";
    const timeLabel = timeframeStart && timeframeEnd
        ? (isRange ? formatDateHMRange(timeframeStart, timeframeEnd) : formatDateHM(timeframeStart))
        : null;
    const currentSliceLabel = t(`Day part restaurant label ${sliceKey}`);
    const datePart = selectedDateIsToday ? "" : formatDayMonth(date);
    const label = sliceKey === "now"
        ? ([datePart, timeLabel].filter(Boolean).join(" ") || currentSliceLabel)
        : !selectedDateIsToday
            ? `${currentSliceLabel}: ${datePart}${timeLabel ? ` ${timeLabel}` : ""}`
            : timeLabel
                ? `${currentSliceLabel}: ${timeLabel}`
                : currentSliceLabel;

    const sliceSubLabel = (slice: FloorplanDaySlice) => {
        const range = slice === "custom"
            ? `${customTimeframe.start} - ${customTimeframe.end}`
            : slice === "now"
                ? formatDateHM(new Date())
                : daySlices(slice, "day").join(" - ");
        return `${formatDayMonth(date)} ${range}`;
    };

    return (
        <Box
            sx={{
                position: "absolute",
                top: {xs: 8, sm: 12},
                left: {xs: 8, sm: 12},
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: isAtNow ? "divider" : "warning.main",
                    borderRadius: 2,
                }}
            >
                <Tooltip
                    title={t("Part of the day")}
                    enterDelay={300}
                    disableInteractive
                >
                    <ButtonBase
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={Boolean(menuAnchor)}
                        onClick={event => setMenuAnchor(event.currentTarget)}
                        sx={{
                            px: {xs: 1, sm: 1.25},
                            height: {xs: 28, sm: 32},
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            borderRadius: 2,
                            textAlign: "left",
                            "&:hover": {bgcolor: "action.hover"},
                        }}
                    >
                        <AccessTimeIcon sx={{fontSize: {xs: 14, sm: 16}}}/>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: {xs: 11, sm: 13},
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {label}
                        </Typography>
                    </ButtonBase>
                </Tooltip>

                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 220,
                                boxShadow: "0 2px 5px rgba(15, 23, 42, 0.22)",
                            },
                        },
                    }}
                >
                    {FLOORPLAN_DAY_SLICES.map(slice => (
                        <MenuItem
                            key={slice}
                            selected={slice === sliceKey}
                            onClick={() => {
                                setReservationDaySlice(slice);
                                setMenuAnchor(null);
                            }}
                        >
                            <Stack sx={{minWidth: 0}}>
                                <Typography sx={{fontWeight: 600}}>
                                    {t(`Day part restaurant label ${slice}`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {sliceSubLabel(slice)}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    ))}
                </Menu>
            </Box>

            {/* The restaurant's availability sections and block periods on the selected day */}
            <DayAvailability/>
        </Box>
    );
};

export default Timeframe;
